import React from 'react';

import { get } from '@lumapps/constants';
import { ToastType } from '@lumapps/constants/types';
import { useDispatch } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';

import { success, info, warning, error, extended, updateExtended, remove } from '../ducks/thunks';
import { ExtendedNotificationAPI, NotificationsAPI } from '../ducks/types';

const { isWebComponentsApp } = get();

export type UseNotification = {
    success: (options: NotificationsAPI) => void;
    info: (options: NotificationsAPI) => void;
    warning: (options: NotificationsAPI) => void;
    error: (options: NotificationsAPI) => void;
    extended: (options: ExtendedNotificationAPI) => void;
    updateExtended: (options: { id: string; notification: Partial<ExtendedNotificationAPI> }) => void;
    remove: (id: string) => void;
};

/**
 * Toast notification dispatch actions
 */
export const useNotification = (): UseNotification => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const notifications = React.useMemo(
        () => ({
            success: (options: NotificationsAPI) => {
                dispatch(success(options));
            },
            info: (options: NotificationsAPI) => {
                dispatch(info(options));
            },
            warning: (options: NotificationsAPI) => {
                dispatch(warning(options));
            },
            error: (options: NotificationsAPI) => {
                dispatch(error(options));
            },
            extended: (options: ExtendedNotificationAPI) => {
                dispatch(extended(options));
            },
            updateExtended: (options: { id: string; notification: Partial<ExtendedNotificationAPI> }) => {
                dispatch(updateExtended(options));
            },
            remove: (id: string) => {
                dispatch(remove(id));
            },
        }),
        [dispatch],
    );

    if (isWebComponentsApp) {
        // In the web components app, we use the BeekeeperContext to show notifications
        return {
            success: (options: NotificationsAPI) => {
                const content = translate(options.translate);
                window.BeekeeperContext?.showToast(content || '', ToastType.SUCCESS);
            },
            info: (options: NotificationsAPI) => {
                const content = translate(options.translate);
                window.BeekeeperContext?.showToast(content || '', ToastType.INFORMATION);
            },
            warning: (options: NotificationsAPI) => {
                const content = translate(options.translate);
                window.BeekeeperContext?.showToast(content || '', ToastType.WARNING);
            },
            error: (options: NotificationsAPI) => {
                const content = translate(options.translate);
                window.BeekeeperContext?.showToast(content || '', ToastType.ERROR);
            },
            extended: () => {
                // eslint-disable-next-line no-console
                console.warn('Extended notifications are not supported in web components.');
            },
            updateExtended: () => {
                // eslint-disable-next-line no-console
                console.warn('Extended notifications are not supported in web components.');
            },
            remove: () => {
                // eslint-disable-next-line no-console
                console.warn('Extended notifications are not supported in web components.');
            },
        };
    }

    return notifications;
};
